import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Media } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { GET_MEDIA } from 'graphql/media/queries/getMedia.gql';
import { useAuthContext } from 'context/auth.context';
import { CREATE_MEDIA_COMMENT } from 'graphql/media/mutations/createMediaComment.gql';

export interface MediaInterface extends Media {
  signedUrl: string;
  fileType: string;
  step: {
    subject: string;
    activity: {
      id: number;
      store: {
        name: string;
      };
    };
  };
  mediaComments: {
    id: number;
    body: string;
    user: {
      id: number;
      firstName: string;
      lastName: string;
    };
    createdAt: Date;
  }[];
}

export interface CreateCommentInterface {
  mediaId: number;
  body: string;
}

export interface MediaGalleryFilter {
  tenantId?: number;
  storeId?: number;
  createdDate?: Date;
}

interface MediaGalleryPageContextInterface {
  media: MediaInterface[];
  mediaGalleryFilter: MediaGalleryFilter;
  setMediaGalleryFilter: (filter: MediaGalleryFilter) => void;
  getMediaById: (id: number) => Media | undefined;
  addMediaComment: (comment: CreateCommentInterface) => void;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
  createCommentLoading: boolean;
}

const defaultContext: MediaGalleryPageContextInterface = {
  media: [],
  mediaGalleryFilter: {},
  setMediaGalleryFilter: () => {},
  getMediaById: () => undefined,
  addMediaComment: (comment: CreateCommentInterface) => {},
  loading: false,
  refetch: () => {},
  createCommentLoading: false,
};

const MediaGalleryPageContext = createContext<MediaGalleryPageContextInterface>(defaultContext);

export const MediaGalleryPageContextProvider = ({ children }: { children: ReactNode }) => {
  const [media, setMedia] = useState<MediaInterface[]>([]);
  const { userTenantId, user } = useAuthContext();
  const [mediaGalleryFilter, setMediaGalleryFilter] = useState<MediaGalleryFilter>({
    tenantId: userTenantId,
  });
  const [createMediaComment, { loading: createCommentLoading }] = useMutation(CREATE_MEDIA_COMMENT);

  const addMediaComment = async (comment: CreateCommentInterface) => {
    await createMediaComment({
      variables: {
        input: {
          ...comment,
          tenantId: userTenantId,
          userId: user?.user.id,
        },
      },
    });
  };

  useMemo(() => {
    setMediaGalleryFilter({
      ...mediaGalleryFilter,
      tenantId: userTenantId,
    });
  }, [userTenantId]);

  const { loading, error, refetch } = useQuery(GET_MEDIA, {
    skip: !userTenantId || !mediaGalleryFilter.tenantId,
    variables: {
      tenantId: userTenantId,
    },
    onCompleted: ({ media }: { media: MediaInterface[] }) => {
      setMedia(media);
    },
  });

  const getMediaById = (id: number) => {
    return media.find((file) => file.id === id);
  };

  return (
    <MediaGalleryPageContext.Provider
      value={{
        media,
        mediaGalleryFilter,
        setMediaGalleryFilter,
        getMediaById,
        addMediaComment,
        loading,
        error,
        refetch,
        createCommentLoading,
      }}
    >
      {children}
    </MediaGalleryPageContext.Provider>
  );
};

export const useMediaGalleryPageContext = () => useContext(MediaGalleryPageContext);
